<template>
  <div v-if="cards.length" id="cards" class="cards-grid-wrapper">
    <ul class="cards-grid" data-cy="cards-grid">
      <li v-for="card in cards" :key="card.id" class="cards-item ratio-1-1">
        <div class="card ratio-1-1" :style="{ backgroundImage: `url(${getReducedContentfulImage(card.imageUrl)})` }">
          <a class="card-link" :href="getLinkHrefForCard(card)" target="_blank" :title="getTranslation(card.title)">
            <div class="type">{{ getTranslation(card.type) || 'Asset' }}</div>
            <div class="title">{{ getTranslation(card.title) }}</div>
            <a :href="card.link['en-US']" class="see-more-button">
              <backstage-button icon-right="chevron-right">
                {{ $t('marketing.card_button') }}
              </backstage-button>
            </a>
          </a>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useLocale } from '@/composables/locale';
import { CardModel } from '@/models/card.model';
import { LinkTypeEnum } from '@/models/link-type.enum';
import { CardsService } from '@/services/cards.service';
import { useUserStore } from '@/stores/user';
import { LocalizedString } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'cards-grid',
  components: {
    'backstage-button': BackstageButton,
  },
  props: {
    variant: {
      default: 'marketing',
      type: String,
    },
    pageId: {
      required: true,
      type: String,
      default: '',
    },
  },
  setup() {
    const { userLanguage } = useLocale();
    const { selectedAccountId } = storeToRefs(useUserStore());

    return {
      userLanguage,
      accountId: selectedAccountId,
    };
  },
  data() {
    return {
      cards: new Array<CardModel>(),
      cardsService: new CardsService(),
    };
  },
  computed: {
    linkType() {
      return LinkTypeEnum;
    },
  },
  mounted() {
    this.fetchCards();
  },
  methods: {
    getTranslation(field: LocalizedString): string {
      if (!field) {
        return 'Asset';
      }

      return field[this.userLanguage];
    },
    getLinkHrefForCard(card: CardModel) {
      return card.linkType === LinkTypeEnum.email
        ? `mailto:${card.link[this.userLanguage]}`
        : card.link[this.userLanguage];
    },
    getReducedContentfulImage(imageUrl: string) {
      return imageUrl
        .replace('downloads.ctfassets.net', 'images.ctfassets.net')
        .concat('?w=2000&h=2000&fl=progressive&q=90');
    },
    async fetchCards() {
      const validation = await this.cardsService.getCards(this.pageId, this.accountId);

      if (validation.isValid() && validation.model) {
        this.cards = validation.model.sort((a, b) => a.order - b.order);
      }
    },
  },
});
</script>
<style scoped lang="scss">
.cards-grid-wrapper {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: 1920px;
  padding: 30px 20px 32px 20px;

  .cards-grid {
    display: grid;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 0 20px;
    justify-content: center;
    grid-template-columns: repeat(auto-fill, minmax(460px, 460px));
    grid-auto-columns: auto;
    grid-gap: 20px;
    row-gap: 20px;
    justify-items: center;

    @include breakpoint(xxlarge) {
      grid-template-columns: repeat(auto-fill, minmax(calc(100% / 4), 1fr));
    }

    .cards-item {
      min-width: 460px;
      min-height: 460px;
      width: 100%;
      height: 100%;

      @media (max-width: 40rem) {
        width: 366px;
      }

      &.ratio-1-1 {
        aspect-ratio: 1;
      }

      &.ratio-3-5 {
        aspect-ratio: 3 / 5;
      }

      @media (hover) {
        .card:hover {
          .card-link {
            .see-more-button {
              display: block;
              opacity: 1;
              visibility: visible;
            }
          }
        }
      }

      .card {
        display: flex;
        background-color: var(--primary-bg-color);
        background-position: center center;
        background-repeat: no-repeat;
        background-size: cover;
        position: relative;
        border-radius: 4px;

        &.ratio-1-1 {
          aspect-ratio: 1 / 1;
        }

        &.ratio-3-5 {
          aspect-ratio: 3 / 5;
        }

        .card-link {
          position: absolute;
          left: 40px;
          bottom: 40px;
          color: var(--white-color);
          text-align: left;

          .type {
            font-size: 0.875rem;
          }

          .title {
            font-size: 2.0625rem;
            margin-bottom: 0.5rem;
          }

          @media (hover) {
            .see-more-button {
              opacity: 0;
              visibility: hidden;
              transition: opacity 0.3s;
            }
          }
        }
      }
    }
  }
}
</style>
